import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faCalendar,
  faTimes,
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faTicketAlt,
  faCreditCard,
  faAddressCard
} from "@fortawesome/free-solid-svg-icons";

const ToastNotification = ({ notification, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  }, [onClose]);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 10);

    // Auto-dismiss after 5 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [handleClose]);

  const getStyles = () => {
    switch (notification.type) {
      case "success":
        return { icon: faCheckCircle, color: "bg-emerald-500" };
      case "error":
        return { icon: faExclamationCircle, color: "bg-rose-500" };
      case "warning":
        return { icon: faExclamationTriangle, color: "bg-amber-500" };
      case "booking":
        return { icon: faCalendar, color: "bg-purple-500" };
      case "ticket":
        return { icon: faTicketAlt, color: "bg-orange-500" };
      case "subscription":
        return { icon: faCreditCard, color: "bg-blue-500" };
      case "lead":
        return { icon: faAddressCard, color: "bg-indigo-500" };
      case "contact":
        return { icon: faUser, color: "bg-sky-500" };
      default:
        return { icon: faInfoCircle, color: "bg-slate-500" };
    }
  };

  const { icon, color } = getStyles();

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
      <div
        className={`
          relative mb-3 w-full max-w-sm bg-white rounded-2xl shadow-2xl 
          border border-gray-200 overflow-hidden transform transition-all duration-300 ease-out
          ${isVisible && !isExiting ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}
        `}
        style={{
          animation: isVisible && !isExiting ? "slideInRight 0.3s ease-out" : "",
        }}
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100">
          <div
            className={`h-full ${color} transition-all duration-5000 ease-linear`}
            style={{
              width: isExiting ? "0%" : "100%",
              animation: isVisible && !isExiting ? "shrink 5s linear forwards" : "",
            }}
          />
        </div>

        <div className="p-4 flex items-start gap-3">
          {/* Icon */}
          <div
            className={`${color} text-white rounded-full p-3 flex-shrink-0 shadow-lg`}
          >
            <FontAwesomeIcon icon={icon} className="w-5 h-5" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 text-sm mb-1">
                  {notification.title}
                </h4>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {notification.message}
                </p>
                {notification.time && (
                  <p className="text-xs text-gray-400 mt-1">
                    {notification.time}
                  </p>
                )}
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 p-1"
                aria-label="Close notification"
              >
                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ToastNotification;

